import React, { useEffect, useState } from "react";
import { Briefcase } from "lucide-react";
import { supabase } from "../lib/supabase";
import { EmptyState } from "./ui/empty-state";
import {
  addToTotals,
  emptyTotals,
  fetchTeamAgentUsageDaily,
  rowTotalTokens,
  sumUsage,
  UNASSIGNED_PROJECT_KEY,
  type UsageTotals,
} from "../lib/agentUsage";
import { UsageCostFootnote, UsageCostText } from "./UsageCost";
import { formatModelLabelShort } from "../lib/modelLabel";
import { getModelColor } from "../lib/modelColors";
import { CardContent, CardHeader, CardTitle, CardDescription } from "./ui/card";
import { Skeleton } from "./ui/skeleton";
import { rangeLabel, rangeLabelAsOf } from "../lib/dayLabel";

// ============================================================================
// Component: TopProjects
//
// 2026-09-09: re-pointed from git_repo-derived buckets to REAL admin-created
// projects (see 20260909100000_add_projects.sql / Projects.tsx). Bucketing
// is now by agents.project_id -- each agent is pinned to at most one project
// (a fixed, self-service assignment made from the agent's own detail page,
// or at "Add agent" time), and every one of that agent's check-ins in the
// window rolls up into that project's bucket. Agents with no project_id set
// (not yet assigned, or the admin hasn't created projects yet) are bucketed
// under "Unassigned" rather than dropped, matching TopModelsUsed's "Unknown
// model" precedent -- distinct from the old "Unknown project" label, which
// meant "no git_repo detected" and no longer applies now that the bucket key
// is a real FK instead of a free-text repo string.
//
// 2026-09-09 (same day): each bar is now SEGMENTED BY MODEL. Bar length
// still ranks by summed estimated $ (unchanged — that's the "which project
// is costing us" question this card answers), but the fill is split into
// per-model slices sized by that model's share of the project's TOKEN
// volume (input + output + cache tokens), not its share of $ — the ask was
// literally "which model has used how many tokens," and cost-splitting the
// same bar would double up with the $ label already shown per row. Colors
// come from src/lib/modelColors.ts's getModelColor — the same deterministic
// per-model color TokenSpendOverTime.tsx and TopModelsUsed.tsx use, so a
// model reads as the same color on every dashboard card, not just "the same
// color across renders of this one widget."
//
// modelOrder (biggest token consumer across the visible rows, first) still
// drives the LEGEND's left-to-right order and which models get a slot at
// all — just not their color anymore.
//
// A single project can span multiple models, so usage rows are summed into
// their project bucket (UsageTotals) and the bucket keeps fallback-priced /
// unpriced event counts, so a per-project total is never silently short.
//
// NOT admin-gated, matching TopBranches'/TopModelsUsed's reasoning: a
// project name isn't attributable to a specific person the way per-agent or
// per-human token spend is.
//
// $ shown is a LIST-PRICE ESTIMATE. 2026-10-02 (Step 6 of
// docs/TOKEN_COST_ACCURACY_PLAN.md): tokens and $ now come from
// get_team_agent_usage_daily via src/lib/agentUsage.ts (priced server-side
// per usage event, cache tokens included) instead of
// get_team_agent_checkin_aggregates + a client-side rate table.
//
// 2026-10-02 (later): bucketed by the RPC's own project_id column (the
// agent's current agents.project_id) instead of joining agent -> project on
// the client. That matters because the RPC returns agent_id NULL for agents
// the caller can't see in detail -- those rows still carry project_id, so
// they're counted here and project totals add up to the same team total
// TopModelsUsed shows. null project_id = "Unassigned".
//
// 2026-09-09 (later same day): restyled the fill from a single continuous
// pill into a row of discrete "ticks" (a segmented/equalizer look), per a
// reference screenshot — each row is TICK_COUNT fixed-width bars, some
// colored (this row's fill, split by model) and the rest bg-muted. Scaled
// against the plain row max (top-ranked row = all ticks filled) — an
// earlier pass scaled against max*1.3 for cosmetic headroom, but that was
// reverted: the top row filling completely is the correct read of "ranked
// by spend," and inventing headroom understated it.
//
// 2026-09-09 (later still): dropped the trailing-7-day window in favor of
// the dashboard's date selector — took a `date` prop and filtered
// checkin_date to exactly that day. Kept OUT of this change:
// TokenSpendOverTime, which stays a per-day trend chart (collapsing it to
// one selected day would leave it with a single bar) — see the 2026-09-09
// conversation. It briefly (2026-09-13) had its own independent start/end
// range picker instead of sharing the dashboard's selector, but that was
// reverted 2026-09-14 — two independent date controls on one page caused a
// hierarchy problem (see TokenSpendOverTime.tsx's comment).
//
// 2026-09-17: takes the dashboard's full `range` ({from, to} date-key
// strings) instead of just its single end date — filters checkin_date with
// `.gte`/`.lte` so a multi-day selection actually rolls up everything in
// it, not just the last day. Picking a single day still collapses
// from === to, so nothing changes for that case.
// ============================================================================

const TICK_COUNT = 28;

const tokenCompactFormatter = new Intl.NumberFormat("en-US", {
  notation: "compact",
  maximumFractionDigits: 1,
});

interface TopProjectsProps {
  teamId: string;
  range: { from: string; to: string }; // YYYY-MM-DD, the dashboard's selected range
  // Pod filter (NewDashboard.tsx's pod dropdown, admin/manager only). Only
  // ever populated for admin/manager callers -- the RPC masks agent_id to
  // null for plain members, so this stays undefined for them and the widget
  // is unfiltered, same as before the pod filter existed.
  agentIds?: string[];
}

interface ModelSlice {
  model: string;
  tokens: number;
  color: string;
}

interface ProjectRow {
  project: string;
  label: string;
  usage: UsageTotals;
  totalTokens: number;
  modelSlices: ModelSlice[];
}

const costOf = (row: { usage: UsageTotals }) => row.usage.costUsd ?? 0;

const TopProjects: React.FC<TopProjectsProps> = ({ teamId, range, agentIds }) => {
  const [rows, setRows] = useState<ProjectRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [totals, setTotals] = useState<UsageTotals>(emptyTotals());

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      setLoading(true);
      setError(null);

      // usage_date (team-local) — the usage equivalent of checkin_date, so
      // "the selected range" means the same thing across every card.
      const [projectsResult, usageResult] = await Promise.all([
        (supabase.from as any)("projects")
          .select("id, name")
          .eq("team_id", teamId)
          .is("deleted_at", null),
        fetchTeamAgentUsageDaily(
          teamId,
          range.from,
          range.to,
          agentIds ? { agentIds } : undefined,
        ).then(
          (rows) => ({ rows, error: null as unknown }),
          (error: unknown) => ({ rows: [], error }),
        ),
      ]);

      if (cancelled) return;

      if (projectsResult.error || usageResult.error) {
        console.error(
          "Failed to load top projects:",
          projectsResult.error ?? usageResult.error,
        );
        setError("Could not load top projects.");
        setLoading(false);
        return;
      }

      const projectNameById = new Map<string, string>(
        ((projectsResult.data ?? []) as { id: string; name: string }[]).map((p) => [
          p.id,
          p.name,
        ]),
      );

      const totalsByProject = new Map<
        string,
        {
          usage: UsageTotals;
          tokensByModel: Map<string, number>;
        }
      >();

      for (const row of usageResult.rows) {
        // Includes agent_id-null rows: this is a by-project team total.
        const key = row.projectId ?? UNASSIGNED_PROJECT_KEY;
        const current = totalsByProject.get(key) ?? {
          usage: emptyTotals(),
          tokensByModel: new Map<string, number>(),
        };
        addToTotals(current.usage, row);
        // "unknown" (lowercase), not "Unknown model" — same bucket key
        // TopModelsUsed/TokenSpendOverTime use, so getModelColor("unknown")
        // resolves to the same color there as it does here.
        const modelKey = row.model || "unknown";
        current.tokensByModel.set(
          modelKey,
          (current.tokensByModel.get(modelKey) ?? 0) + rowTotalTokens(row),
        );
        totalsByProject.set(key, current);
      }

      const topFive = Array.from(totalsByProject.entries())
        .map(([project, sums]) => ({
          project,
          label:
            project === UNASSIGNED_PROJECT_KEY
              ? "Unassigned"
              : (projectNameById.get(project) ?? "Deleted project"),
          usage: sums.usage,
          totalTokens: sums.usage.totalTokens,
          tokensByModel: sums.tokensByModel,
        }))
        .sort((a, b) => costOf(b) - costOf(a) || b.totalTokens - a.totalTokens)
        .slice(0, 5);

      // Order models by biggest total-token consumer (across these rows)
      // first — this still decides the legend's left-to-right order and
      // which models appear in each row's modelSlices, just not their
      // color anymore (that's getModelColor, keyed off the model id itself).
      const globalTokensByModel = new Map<string, number>();
      for (const p of topFive) {
        for (const [model, tokens] of p.tokensByModel.entries()) {
          globalTokensByModel.set(model, (globalTokensByModel.get(model) ?? 0) + tokens);
        }
      }
      const modelOrder = Array.from(globalTokensByModel.entries())
        .sort((a, b) => b[1] - a[1])
        .map(([model]) => model);

      const ranked: ProjectRow[] = topFive.map((p) => ({
        project: p.project,
        label: p.label,
        usage: p.usage,
        totalTokens: p.totalTokens,
        modelSlices: modelOrder
          .filter((model) => p.tokensByModel.has(model))
          .map((model) => ({
            model,
            tokens: p.tokensByModel.get(model) as number,
            color: getModelColor(model),
          })),
      }));

      setRows(ranked);
      setTotals(sumUsage(usageResult.rows));
      setLoading(false);
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [teamId, range.from, range.to, agentIds?.join(",")]);

  // Scale by $ when anything was priced; fall back to tokens when nothing in
  // view had a price (otherwise every bar would be empty).
  const anyPriced = rows.some((r) => r.usage.costUsd !== null);
  const scaleValue = (r: ProjectRow) => (anyPriced ? costOf(r) : r.totalTokens);
  const scaleMax = Math.max(...rows.map(scaleValue), anyPriced ? 0.01 : 1);

  // Legend covers every model appearing across the visible rows, in the
  // same order/color as the segments themselves, labeled with the short
  // "Opus 4.8"-style form (no "Claude" prefix — redundant next to a
  // "Claude Code" client_name elsewhere on the dashboard).
  const legend = new Map<string, string>();
  for (const row of rows) {
    for (const slice of row.modelSlices) {
      if (!legend.has(slice.model)) legend.set(slice.model, slice.color);
    }
  }

  return (
    <div className="flex h-full flex-col">
      <CardHeader>
        <CardTitle className="text-base">Top projects</CardTitle>
        <CardDescription>Ranked by list-price estimate — {rangeLabel(range.from, range.to)}</CardDescription>
      </CardHeader>
      <div className="border-t border-border" />
      <CardContent className="flex flex-1 flex-col space-y-4 pt-4">
        {loading ? (
          <>
            {[0, 1, 2, 3].map((i) => (
              <div key={i} className="space-y-1.5">
                <div className="flex flex-wrap items-baseline justify-between gap-1">
                  <Skeleton className="h-4 w-32" />
                  <Skeleton className="h-3 w-24" />
                </div>
                <Skeleton className="h-6 w-full" />
              </div>
            ))}
          </>
        ) : error ? (
          <p className="text-sm text-destructive">{error}</p>
        ) : rows.length === 0 ? (
          <EmptyState
            icon={Briefcase}
            message={`No connector activity ${rangeLabelAsOf(range.from, range.to)}.`}
          />
        ) : (
          <>
            {rows.map((row) => {
              // Discretize the continuous fill fraction into TICK_COUNT
              // whole ticks, then color that many ticks in model order,
              // splitting proportionally by token share (largest remainder
              // goes to the last model so the tick count always adds up
              // exactly, never short or over by rounding).
              const filledTicks = Math.round(
                (scaleValue(row) / scaleMax) * TICK_COUNT,
              );
              const tickColors: string[] = [];
              let remaining = filledTicks;
              row.modelSlices.forEach((slice, i) => {
                const isLast = i === row.modelSlices.length - 1;
                const count = isLast
                  ? remaining
                  : Math.min(
                      remaining,
                      Math.round((slice.tokens / (row.totalTokens || 1)) * filledTicks),
                    );
                for (let k = 0; k < count; k++) tickColors.push(slice.color);
                remaining -= count;
              });

              return (
                <div key={row.project} className="space-y-1.5">
                  <div className="flex flex-wrap items-baseline justify-between gap-1 text-sm">
                    <span className="truncate text-foreground">{row.label}</span>
                    <span className="text-xs text-muted-foreground">
                      <UsageCostText totals={row.usage} />
                      {" · "}
                      {tokenCompactFormatter.format(row.totalTokens)} tokens
                    </span>
                  </div>
                  <div className="flex h-6 gap-[3px] rounded-md bg-background">
                    {Array.from({ length: TICK_COUNT }).map((_, i) => (
                      <div
                        key={i}
                        className="h-full flex-1 rounded-sm"
                        style={{
                          // Keep unfilled ticks visible against the cream
                          // dashboard background without adding a separate
                          // track or changing the filled model colors.
                          backgroundColor: tickColors[i] ?? "hsl(var(--foreground) / 0.18)",
                        }}
                      />
                    ))}
                  </div>
                </div>
              );
            })}
            {legend.size > 0 && (
              <div className="flex flex-wrap gap-x-3 gap-y-1.5 border-t border-border pt-3">
                {Array.from(legend.entries()).map(([model, color]) => (
                  <div key={model} className="flex items-center gap-1.5">
                    <span
                      className="h-3 w-3 shrink-0 rounded-sm"
                      style={{ backgroundColor: color }}
                    />
                    <span className="truncate text-xs text-muted-foreground">
                      {model === "unknown" ? "Unknown model" : formatModelLabelShort(model)}
                    </span>
                  </div>
                ))}
              </div>
            )}
            <UsageCostFootnote totals={totals} />
          </>
        )}
      </CardContent>
    </div>
  );
};

export default TopProjects;
